<script setup lang="ts">
import type { PlatformSaleItemEntity } from '~/types/sale.type';
import { isAuction } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
}
const { sale } = defineProps<Props>();

// COMPOSABLES
const { tt, tn } = useTypedI18n();

// DATA
const offers = computed(() =>
    isAuction(sale)
        ? { count: sale.numberOfBids, icon: 'lucide:gavel', label: tt('sdp.socialProof.numberOfBids', { count: sale.numberOfBids }) }
        : {
              count: sale.activeNegotiationCounter ?? 0,
              icon: 'lucide:handshake',
              label: tt('sdp.socialProof.numberOfNegotiations', { count: sale.activeNegotiationCounter ?? 0 }),
          }
);

const visitorsCount = computed((): number => sale.visitorsCount ?? 0);

const showOffers = computed((): boolean => offers.value.count > 0);
const showVisitors = computed((): boolean => visitorsCount.value > 0);
const isVisible = computed((): boolean => showOffers.value || showVisitors.value);
</script>

<template>
    <div v-if="isVisible" class="flex rounded-xl bg-neutral-100">
        <!-- Bids (auction) / active negotiations (direct sale): primary, higher relevance -->
        <div v-if="showOffers" class="flex flex-1 flex-col items-center gap-1 p-4">
            <div class="text-primary flex items-center gap-2">
                <UIcon :name="offers.icon" class="size-5" />
                <span class="text-xl font-bold">{{ tn(offers.count, 'integer') }}</span>
            </div>
            <p class="text-muted text-center text-xs font-medium whitespace-nowrap uppercase">{{ offers.label }}</p>
        </div>

        <div v-if="showOffers && showVisitors" class="my-4 w-px bg-neutral-300" />

        <!-- Visitors: default (black) -->
        <div v-if="showVisitors" class="text-highlighted flex flex-1 flex-col items-center gap-1 p-4">
            <div class="flex items-center gap-2">
                <UIcon name="lucide:eye" class="size-5" />
                <span class="text-xl font-bold">{{ tn(visitorsCount, 'integer') }}</span>
            </div>
            <p class="text-muted text-center text-xs font-medium whitespace-nowrap uppercase">
                {{ tt('sdp.socialProof.visitorsCount', { count: visitorsCount }) }}
            </p>
        </div>
    </div>
</template>
